import { DOMAdapter, ExtensionType, LoaderParserPriority, Texture, type LoaderParser } from 'pixi.js';
import DOMPurify from 'dompurify';

import { constructSVGAssetKey } from 'js/shared/lib/LocalDatabase/keys';
import ScribeImageElementModel from 'js/models/ScribeImageElementModel';
import { getConvertedSVGData, storeConvertedSVGData } from 'js/shared/lib/LocalDatabase';
import svgSplitter from 'js/playback/helpers/svgSplitter';
import svgToDataUrl from 'js/shared/helpers/svgToDataUrl';
import setupSVGElement from 'js/playback/helpers/setupSVGElement';
import { PlaybackScribeModel } from 'js/types';
import { getImagePixelsFromUrl } from 'js/shared/helpers/getImagePixelsFromUrl';
import { isVectorizedImage } from 'js/playback/helpers/isVectorizedImage/isVectorizedImage';
import { convert } from 'js/shared/lib/SVGConvert';
import { generateFallbackRevealPath } from 'js/playback/helpers/generateFallbackRevealPath';
import { isRevealAnimationType } from 'js/playback/helpers/drawAnimationType';

import { getDrawCommandsFromSVGElements, PointWithPathLength } from '../helpers/getDrawCommandsFromSVGElements';
import { getClampedSvgSize } from '../helpers/getClampedSvgSize';
import { getMaxCameraZoomForSceneImageIsIn } from '../helpers/getMaxCameraZoomForSceneImageIsIn';

export type DrawCommands = {
  points: PointWithPathLength[];
  totalLength: number;
  brushSize: number;
}[];

export interface SplitSvgAsset {
  fullTexture: Texture;
  fullSvgUrl: string;
  pixels: Uint8ClampedArray;
  isVectorizedImage: boolean;
  hasColorsAndStrokes: boolean;
  hasColorsAndNoRevealPaths: boolean;
  getSplitTextures: () => Promise<{
    fillsTexture: Texture;
    strokesTexture: Texture;
    getStrokesDrawCommands: (element: ScribeImageElementModel) => Promise<DrawCommands>;
    getFillsDrawCommands: (element: ScribeImageElementModel) => Promise<DrawCommands>;
  }>;
  destroySplitTextures: () => void;
}

interface AssetMetadata {
  type: 'splitSVG';
  element: ScribeImageElementModel;
  project: PlaybackScribeModel;
}

type SvgParts = ReturnType<typeof svgSplitter>;

interface ParsedViewBox {
  minX: number;
  minY: number;
  viewBoxOffsetX: number;
  viewBoxOffsetY: number;
}

interface SvgProcessingContext {
  svgParts: SvgParts;
  viewBox: string;
  parsedViewBox: ParsedViewBox;
  pixels: Uint8ClampedArray;
  vectorizedImage: boolean;
  element: ScribeImageElementModel;
  svgWidth: number;
  svgHeight: number;
}

function parseViewBox(viewBox: string): ParsedViewBox {
  const [minX, minY, viewBoxOffsetX, viewBoxOffsetY] = viewBox.split(' ');
  return {
    minX: Number(minX),
    minY: Number(minY),
    viewBoxOffsetX: Number(viewBoxOffsetX),
    viewBoxOffsetY: Number(viewBoxOffsetY)
  };
}

function createPartsSvgUrl({
  parts,
  width,
  height,
  viewBox
}: {
  parts: Array<Element>;
  viewBox: string;
  width: number;
  height: number;
}): string {
  const svg = setupSVGElement(parts, width, height, viewBox);
  return svgToDataUrl(svg) as string;
}

async function loadTextureFromUrl(dataUrl: string): Promise<Texture> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = dataUrl;
  await img.decode();

  return Texture.from(img);
}

function buildSplitSvgAsset(ctx: SvgProcessingContext): SplitSvgAsset {
  const { svgParts, viewBox, parsedViewBox, pixels, vectorizedImage, element, svgWidth, svgHeight } = ctx;
  const { minX, minY, viewBoxOffsetX, viewBoxOffsetY } = parsedViewBox;

  const viewboxScaleXFactor = element.width / viewBoxOffsetX;
  const viewboxScaleYFactor = element.height / viewBoxOffsetY;

  const hasColorsAndStrokes = !!svgParts.strokes.length && !!svgParts.fills.length;
  const hasColorsAndNoRevealPaths = !svgParts.revealPaths.length && !!svgParts.fills.length;

  const fillsWithMetadata = [...svgParts.meta, ...svgParts.fills];
  const strokesWithMetadata = [...svgParts.meta, ...svgParts.strokes];

  const createSvgUrls = () => ({
    fillsUrl: createPartsSvgUrl({ parts: fillsWithMetadata, width: svgWidth, height: svgHeight, viewBox }),
    strokesUrl: createPartsSvgUrl({ parts: strokesWithMetadata, width: svgWidth, height: svgHeight, viewBox })
  });

  let cachedSplitTextures: { fillsTexture: Texture; strokesTexture: Texture } | null = null;

  const getStrokesDrawCommands = async (el: ScribeImageElementModel): Promise<DrawCommands> => {
    if (isRevealAnimationType(el)) {
      return [];
    }
    return vectorizedImage
      ? await generateFallbackRevealPath({ element, pixels })
      : getDrawCommandsFromSVGElements(svgParts.strokes, minX, minY, viewboxScaleXFactor, viewboxScaleYFactor);
  };

  const getFillsDrawCommands = async (el: ScribeImageElementModel): Promise<DrawCommands> => {
    if (isRevealAnimationType(el) || hasColorsAndNoRevealPaths) {
      return await generateFallbackRevealPath({
        element,
        pixels,
        dontIllustrate: hasColorsAndStrokes
      });
    }
    return getDrawCommandsFromSVGElements(svgParts.revealPaths, minX, minY, viewboxScaleXFactor, viewboxScaleYFactor);
  };

  return {
    fullTexture: null as unknown as Texture, // Will be set after loading
    fullSvgUrl: '', // Will be set after loading
    getSplitTextures: async () => {
      if (!cachedSplitTextures) {
        const { fillsUrl, strokesUrl } = createSvgUrls();
        const [fillsTexture, strokesTexture] = await Promise.all([
          loadTextureFromUrl(fillsUrl),
          loadTextureFromUrl(strokesUrl)
        ]);
        cachedSplitTextures = { fillsTexture, strokesTexture };
      }
      return { ...cachedSplitTextures, getStrokesDrawCommands, getFillsDrawCommands };
    },
    destroySplitTextures: () => {
      cachedSplitTextures?.fillsTexture.destroy(true);
      cachedSplitTextures?.strokesTexture.destroy(true);
      cachedSplitTextures = null;
    },
    pixels,
    isVectorizedImage: vectorizedImage,
    hasColorsAndStrokes,
    hasColorsAndNoRevealPaths
  };
}

export const splitSvgLoader: LoaderParser<SplitSvgAsset, AssetMetadata> = {
  id: 'splitSVGloader',
  extension: {
    type: ExtensionType.LoadParser,
    priority: LoaderParserPriority.High
  },

  test(_url, resolvedAsset): boolean {
    return resolvedAsset?.data?.type === 'splitSVG';
  },

  async load(url, asset) {
    const assetId = asset?.data?.element.image?.assetId;
    const element = asset?.data?.element;
    const project = asset?.data?.project;

    if (!assetId || !element || !project) {
      throw new Error('Required data is missing for split SVG loader');
    }

    const indexedDbCacheKey = constructSVGAssetKey({
      assetId,
      recolorSettings: element.image?.recolor,
      useContentSvgViewbox: !!element.useContentSvgViewbox
    });
    const dbCached = await getConvertedSVGData(indexedDbCacheKey);
    const { width, height, scaleX, scaleY } = element;
    const maxCameraZoom = getMaxCameraZoomForSceneImageIsIn(element, project);
    const svgWidth = getClampedSvgSize(width, scaleX, maxCameraZoom);
    const svgHeight = getClampedSvgSize(height, scaleY, maxCameraZoom);

    let svgParts: SvgParts;
    let viewBox: string;
    let pixels: Uint8ClampedArray;
    let vectorizedImage: boolean;

    if (dbCached) {
      svgParts = svgSplitter(dbCached.data.convertedSVGString);
      viewBox = dbCached.data.viewBox;
      pixels = await getImagePixelsFromUrl({ url: dbCached.data.encodedData });
      vectorizedImage = !!dbCached.data.isVectorizedImage;
    } else {
      const response = await DOMAdapter.get().fetch(url);
      const svgText = await response.text();
      vectorizedImage = isVectorizedImage(svgText);
      const convertedSVGString = convert(svgText);
      const parser = new DOMParser();
      const doc = parser.parseFromString(convertedSVGString, 'text/html');
      const svg = doc.querySelector('svg');

      if (!svg) {
        throw new Error('No SVG found in given document');
      }

      const viewBoxAttr = svg.getAttribute('viewBox');
      if (!viewBoxAttr) {
        throw new Error('No viewbox attribute on supplied SVG');
      }
      viewBox = viewBoxAttr;

      const [, , viewBoxWidth, viewBoxHeight] = viewBox.split(' ');
      // If there are no width or height set on the SVG then set it from the viewbox attributes so
      // PIXI knows how to handle it
      if (svg.getAttribute('width') === null) {
        svg.setAttribute('width', `${viewBoxWidth}px`);
      }
      if (svg.getAttribute('height') === null) {
        svg.setAttribute('height', `${viewBoxHeight}px`);
      }

      svgParts = svgSplitter(convertedSVGString);
      let generatedOutlineSvg;
      if (vectorizedImage) {
        // for vectorized images we have no strokes, so we pixelate the outline,
        // then VSVectorImage will use thinning to generate the draw paths
        const outlineWithMeta = [...svgParts.meta, ...svgParts.strokes];
        generatedOutlineSvg = setupSVGElement(outlineWithMeta, width, height, viewBox);
      }

      // Serialize and base64 encode the SVG image
      const encodedData = svgToDataUrl(generatedOutlineSvg ?? svg);

      await storeConvertedSVGData(indexedDbCacheKey, {
        encodedData,
        viewBox,
        convertedSVGString,
        isVectorizedImage: vectorizedImage
      });

      pixels = await getImagePixelsFromUrl({ url: encodedData });
    }

    const ctx: SvgProcessingContext = {
      svgParts,
      viewBox,
      parsedViewBox: parseViewBox(viewBox),
      pixels,
      vectorizedImage,
      element,
      svgWidth,
      svgHeight
    };

    const result = buildSplitSvgAsset(ctx);

    // Load the full texture and attach it to the result
    const fullTextureRespone = await DOMAdapter.get().fetch(url);
    const fullTextureText = await fullTextureRespone.text();
    const svgSetToTextureSize = fullTextureText
      .replace(/(<svg[^>]*)\s+width="[^"]*"/, '$1')
      .replace(/(<svg[^>]*)\s+height="[^"]*"/, '$1')
      .replace(/(<svg\b)/, `$1 width="${svgWidth}" height="${svgHeight}"`);
    const sanitzed = DOMPurify.sanitize(svgSetToTextureSize);
    const fullSvgBlob = new Blob([sanitzed], { type: 'image/svg+xml' });
    const fullSvgUrl = URL.createObjectURL(fullSvgBlob);
    const fullTexture = await loadTextureFromUrl(fullSvgUrl);

    return { ...result, fullTexture, fullSvgUrl };
  },

  unload(asset: SplitSvgAsset) {
    asset.destroySplitTextures();
    asset.fullTexture.destroy(true);
    URL.revokeObjectURL(asset.fullSvgUrl);
  }
};
